import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate, useParams } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { F0Button as Button, F0TagStatus, F0Text, ProgressBarChart } from '@factorialco/f0-react'
import { CreateBlockProgressDocument, LoadContentDocument } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import Loading from 'modules/core/components/Loading'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { LMSModuleAvailabilityWall } from 'modules/trainings/components/LMSModuleAvailabilityWall'
import ContentConsumer from 'modules/trainings/components/content/Consumer'
import { ContentConsumerCallbacks } from 'modules/trainings/components/content/types'
import { Block } from 'modules/trainings/lib/content/Block'
import { ContentModule } from 'modules/trainings/lib/content/Module'
import { QuizBlock } from 'modules/trainings/lib/content/QuizBlock'
import { graphqlContentMapper } from 'modules/trainings/lib/content/mappers'

const showError = () => app.setFlash({ type: 'wrong', message: i18n.t('common.error') })

export default observer(() => {
  const { trainingId, blockId } = useParams()
  const { employee: currentEmployee } = useCurrentSession()
  const navigate = useNavigate()
  const consumerRef = useRef<ContentConsumerCallbacks>(null)
  const [showEmptyState, setShowEmptyState] = useState(false)
  const [completionTrigger, setCompletionTrigger] = useState(0)

  const { data, loading } = useQuery(LoadContentDocument, {
    variables: {
      trainingId: Number(trainingId),
      employeeId: currentEmployee.id,
    },
  })

  const collection = useMemo(() => {
    const modules = data?.trainings.contentModules
    if (loading || !modules) return null

    const newCollection = graphqlContentMapper.fromGraphQL(modules, Number(trainingId))

    // If blockId in URL, select that specific block
    if (blockId) {
      const block = newCollection.flatItems.find(
        (item) => item instanceof Block && item.id === Number(blockId)
      )
      if (block) newCollection.setSelected(block.uuid)
      return newCollection
    }

    // Check if course hasn't started
    const allBlocks = newCollection.flatItems.filter((item): item is Block => item instanceof Block)
    const courseNotStarted = allBlocks.every((b) => b.status === 'notstarted')

    if (courseNotStarted && newCollection.modules[0]) {
      // Fresh start: show first module intro
      newCollection.setSelected(newCollection.modules[0].uuid)
    } else {
      // Resume: select first incomplete accessible block
      const firstIncomplete = allBlocks.find((b) => b.accesible && b.status !== 'completed')
      if (firstIncomplete) newCollection.setSelected(firstIncomplete.uuid)
    }

    return newCollection
  }, [trainingId, data, loading, blockId])

  const [createBlockProgress] = useMutation(CreateBlockProgressDocument, {
    extractorFn: (mutationData) =>
      mutationData.trainingsMutations.createTrainingsContentBlockProgress,
  })

  const isDone = useMemo(() => {
    if (!collection) return false
    return collection.modules.every((m) => m.blocks.every((b) => b.status === 'completed'))
  }, [collection])

  const nonModuleItems = useMemo(
    () =>
      collection?.flatItems.filter(
        (item) => !(item instanceof ContentModule) && !(item instanceof QuizBlock && !item.form)
      ) ?? [],
    [collection?.flatItems]
  )

  // completionTrigger forces recalculation when a block is completed
  const completedCount = useMemo(
    () =>
      nonModuleItems.filter((item) => item instanceof Block && item.status === 'completed').length,
    [nonModuleItems, completionTrigger]
  )

  const totalItems = nonModuleItems.length
  const percentage = totalItems > 0 ? Math.round((completedCount / totalItems) * 100) : 0

  // selectedItemUuid forces recalculation when the selected item changes
  const selectedItemUuid = collection?.selectedItem?.uuid
  const currentPosition = useMemo(() => {
    if (showEmptyState) return totalItems
    if (!collection?.selectedItem) return 0

    const blockIndex = nonModuleItems.findIndex(
      (item) => item.uuid === collection.selectedItem?.uuid
    )
    if (blockIndex >= 0) return blockIndex + 1

    const flatIndex = collection.flatItems.findIndex(
      (item) => item.uuid === collection.selectedItem?.uuid
    )
    if (flatIndex > 0) {
      for (let i = flatIndex - 1; i >= 0; i--) {
        const item = collection.flatItems[i]
        const blockPosition = nonModuleItems.findIndex((b) => b.uuid === item?.uuid)
        if (blockPosition >= 0) return blockPosition + 1
      }
    }

    return 0
  }, [showEmptyState, totalItems, collection, nonModuleItems, selectedItemUuid])

  const isBlockCompleted =
    collection?.selectedItem instanceof Block && collection.selectedItem.status === 'completed'

  const isFirstModule = collection?.selectedItem === collection?.modules[0]

  useEffect(() => {
    if (isDone && collection && !blockId) {
      collection.setSelected(undefined)
      setShowEmptyState(true)
    }
  }, [isDone, collection, blockId])

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') navigate(-1)
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [navigate])

  const advanceToNext = useCallback(() => {
    if (!collection) return

    const items = collection.flatItems
    const currentIndex = items.findIndex((item) => item.uuid === collection.selectedItem?.uuid)
    const shouldSkip = (item: (typeof items)[number]) => item instanceof QuizBlock && !item.form

    for (let i = currentIndex + 1; i < items.length; i++) {
      const item = items[i]
      if (item && !shouldSkip(item)) {
        if (item instanceof Block) item.accesible = true
        collection.setSelected(item.uuid)
        return
      }
    }

    collection.setSelected(undefined)
    setShowEmptyState(true)
  }, [collection])

  const handleNext = useCallback(async () => {
    if (!collection) return

    if (collection.selectedItem instanceof ContentModule) {
      collection.next()
      return
    }

    if (!(collection.selectedItem instanceof Block)) return

    const currentBlock = collection.selectedItem

    try {
      if (consumerRef.current && !(await consumerRef.current.handleNext())) return

      const result = await createBlockProgress({
        variables: {
          contentBlockId: currentBlock.id,
          employeeId: currentEmployee.id,
        },
      })

      const handled = handleResult(result, showError)

      if (handled.success) {
        currentBlock.status = 'completed'
        currentBlock.accesible = true
        setCompletionTrigger((prev) => prev + 1)
        advanceToNext()
      }
    } catch {
      showError()
    }
  }, [collection, createBlockProgress, currentEmployee.id, advanceToNext])

  const handleRestart = useCallback(() => {
    const firstItem = collection?.flatItems[0]
    if (firstItem) {
      collection?.setSelected(firstItem.uuid)
      setShowEmptyState(false)
    }
  }, [collection])

  if (!collection || loading) return <Loading expand />

  const content = (
    <LMSModuleAvailabilityWall>
      <Box
        position="fixed"
        width="full"
        height="full"
        background="grey000White"
        style={{ zIndex: '999', top: '0', left: '0' }}
      >
        <Box
          position="fixed"
          flexDirection="row"
          justifyContent="spaceBetween"
          alignItems="center"
          padding="s16"
          paddingX="s24"
          background="grey000White"
          height="s64"
          style={{ top: '0', left: '0', right: '0', zIndex: '11' }}
        >
          <Box flexDirection="row" alignItems="center" gap="s16">
            <Box width="s200" gap="s8" flexDirection="row" alignItems="center">
              <F0Text
                content={i18n.t('trainings.content.consumer.progress')}
                variant="description"
              />
              <Box width="full">
                <ProgressBarChart
                  value={completedCount}
                  max={totalItems}
                  label={`${percentage}%`}
                  color="feedback-positive"
                />
              </Box>
            </Box>

            {isBlockCompleted && (
              <F0TagStatus
                text={i18n.t('trainings.content.syllabus.status.completed')}
                variant="positive"
              />
            )}
          </Box>

          <Box flexDirection="row" alignItems="center" gap="s12">
            <F0Text content={`${currentPosition}/${totalItems}`} variant="description" />
            <F0Text content="|" variant="small" />
            <Button
              label={i18n.t('trainings.content.consumer.exit')}
              variant="outline"
              onClick={() => navigate(-1)}
            />
          </Box>
        </Box>

        <Box
          flexDirection="column"
          height="full"
          flexGrow
          overflowY="auto"
          padding="s4"
          paddingTop="s64"
        >
          <ContentConsumer
            selectedItem={collection.selectedItem}
            ref={consumerRef}
            showEmptyState={showEmptyState}
            onBack={isFirstModule ? undefined : () => collection.back()}
            onNext={handleNext}
            onRestart={handleRestart}
          />
        </Box>
      </Box>
    </LMSModuleAvailabilityWall>
  )

  if (typeof document === 'undefined') return content

  const mainElement = document.querySelector('main')
  return mainElement ? createPortal(content, mainElement) : content
})
